/**
 * Fechas en la zona de la app (Europe/Madrid), igual que el backend.
 *
 * El navegador puede estar en cualquier zona; si se usara la local, un
 * registro hecho de viaje caeria en un dia distinto al que dice el servidor.
 */
export const APP_TIMEZONE = "Europe/Madrid";

const isoFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: APP_TIMEZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** Instante -> YYYY-MM-DD en Madrid. */
export function toAppDate(d: Date = new Date()): string {
  return isoFormatter.format(d);
}

export function todayISO(): string {
  return toAppDate(new Date());
}

export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const result = new Date(Date.UTC(y, m - 1, d + days));
  return [
    String(result.getUTCFullYear()).padStart(4, "0"),
    String(result.getUTCMonth() + 1).padStart(2, "0"),
    String(result.getUTCDate()).padStart(2, "0"),
  ].join("-");
}

export function daysBetween(a: string, b: string): number {
  const toUtc = (s: string): number => {
    const [y, m, d] = s.split("-").map(Number) as [number, number, number];
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((toUtc(b) - toUtc(a)) / 86_400_000);
}

/** Dia de la semana con lunes = 0, para cuadrar el calendario. */
export function weekdayIndex(date: string): number {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
}

const longDate = new Intl.DateTimeFormat("es-ES", {
  timeZone: "UTC",
  weekday: "long",
  day: "numeric",
  month: "long",
});

const shortDate = new Intl.DateTimeFormat("es-ES", {
  timeZone: "UTC",
  day: "numeric",
  month: "short",
});

const timeOfDay = new Intl.DateTimeFormat("es-ES", {
  timeZone: APP_TIMEZONE,
  hour: "2-digit",
  minute: "2-digit",
});

const monthName = new Intl.DateTimeFormat("es-ES", {
  timeZone: "UTC",
  month: "short",
});

/** Interpreta YYYY-MM-DD como fecha civil, sin arrastrarla a otra zona. */
function civil(date: string): Date {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d));
}

export function formatLongDate(date: string): string {
  const today = todayISO();
  if (date === today) return "Hoy";
  if (date === addDays(today, -1)) return "Ayer";
  return longDate.format(civil(date));
}

export const formatShortDate = (date: string): string =>
  shortDate.format(civil(date));

export const formatMonth = (date: string): string =>
  monthName.format(civil(date));

/** Hora del registro, a partir del instante ISO que devuelve la API. */
export const formatTime = (isoInstant: string): string =>
  timeOfDay.format(new Date(isoInstant));
